// Pure helpers for the top-up instalment-plan model (tv-w98, Feature B
// foundation). No store reads and no `Date.now()` — the reconciler (tv-opw),
// chase logic (tv-qta) and builder UI (tv-1a9) reuse these against the sim clock.

import { addDays } from 'date-fns';
import type { TopupFrequency, TopupInstalment, TopupPlan } from '@/domain/types';

/** Whole AUD dollars paid so far (sum of `paid` instalments). */
export function topupPaidAud(plan: TopupPlan): number {
  return plan.instalments.reduce(
    (sum, i) => (i.status === 'paid' ? sum + i.amountAud : sum),
    0,
  );
}

/** Whole AUD dollars still owed on the plan (never below zero). */
export function topupOutstandingAud(plan: TopupPlan): number {
  return Math.max(0, plan.totalAud - topupPaidAud(plan));
}

/**
 * The earliest still-`scheduled` instalment whose due date has arrived
 * (`dueIso <= nowIso`) — the one now due for a reminder. `null` when nothing is
 * due yet. Paid/reminded/missed/cancelled instalments are ignored.
 */
export function nextDueInstalment(plan: TopupPlan, nowIso: string): TopupInstalment | null {
  const now = new Date(nowIso).getTime();
  let due: TopupInstalment | null = null;
  for (const i of plan.instalments) {
    if (i.status !== 'scheduled') continue;
    if (new Date(i.dueIso).getTime() > now) continue;
    if (due === null || new Date(i.dueIso).getTime() < new Date(due.dueIso).getTime()) {
      due = i;
    }
  }
  return due;
}

/** True once every instalment is settled (paid or cancelled) and nothing is outstanding. */
export function isTopupPlanComplete(plan: TopupPlan): boolean {
  const allSettled = plan.instalments.every(
    (i) => i.status === 'paid' || i.status === 'cancelled',
  );
  return allSettled && topupOutstandingAud(plan) <= 0;
}

const INTERVAL_DAYS: Record<TopupFrequency, number> = { weekly: 7, fortnightly: 14 };

/**
 * Build the `scheduled` instalment schedule for a plan from a start date, a
 * cadence and a per-instalment amount. Deterministic (date-fns `addDays`, no
 * `Date.now()`). Instalments are `instalmentAud` each except the LAST, which
 * absorbs the remainder so the schedule sums to exactly `totalAud`.
 */
export function generateInstalments(opts: {
  startIso: string;
  frequency: TopupFrequency;
  instalmentAud: number;
  totalAud: number;
}): TopupInstalment[] {
  const { startIso, frequency, instalmentAud, totalAud } = opts;
  if (totalAud <= 0) return [];
  // A whole-plan-in-one instalment: too-small or too-large per-instalment amount.
  if (instalmentAud < 1 || instalmentAud >= totalAud) {
    return [{ dueIso: startIso, amountAud: totalAud, status: 'scheduled' }];
  }
  const intervalDays = INTERVAL_DAYS[frequency];
  const n = Math.ceil(totalAud / instalmentAud);
  const start = new Date(startIso);
  const instalments: TopupInstalment[] = [];
  for (let k = 0; k < n; k += 1) {
    const isLast = k === n - 1;
    instalments.push({
      dueIso: addDays(start, k * intervalDays).toISOString(),
      amountAud: isLast ? totalAud - instalmentAud * (n - 1) : instalmentAud,
      status: 'scheduled',
    });
  }
  return instalments;
}
